import React, { useState, useMemo } from 'react';
import RecordedVideoModal from '../../instructor-dashboard/components/RecordedVideoModal';

// OnlineMeetingType code from Tuningfork Constants.StudyResourceConstant.
const ONLINE_MEETING_TEAMS = 45;

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  attendanceTaken?: boolean;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
  zoomMeetingId?: string | null;
  onlineMeetingType?: number | null;
  onlineJoinUrl?: string | null;
  hostEmail?: string | null;
  confirmedByUserId?: number | null;
  confirmedAt?: string | null;
  substitution?: {
    id: number;
    status: string;
    substituteInstructorId: number | null;
    substituteInstructorName: string | null;
    substituteTopicId: number | null;
    substituteTopicName: string | null;
    substituteAgenda: string | null;
    reason: string | null;
  } | null;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface LinearSlotListProps {
  slots: ActivitySlot[];
  isEditMode: boolean;
  onEdit: (slot: ActivitySlot) => void;
  onDelete: (slotId: number) => void;
  onBulkDelete?: (slotIds: number[]) => void;
  onSwap?: (slotIdA: number, slotIdB: number) => void;
  onAdd: (date?: string) => void;
  templateBatchIds?: number[];
  batchMappings?: BatchMapping[];
  onAttendance?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  // Temporary parallel callback for the Node-based attendance screen. Remove
  // along with the corresponding "Attendance 2" button once /attendance-node
  // is fully validated.
  onAttendanceNode?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  onSubstitute?: (slot: ActivitySlot) => void;
  onConfirm?: (slotId: number) => void;
  onUnconfirm?: (slotId: number) => void;
}

const LinearSlotList: React.FC<LinearSlotListProps> = ({
  slots,
  isEditMode,
  onEdit,
  onDelete,
  onBulkDelete,
  onSwap,
  onAdd,
  templateBatchIds = [],
  batchMappings = [],
  onAttendance,
  onAttendanceNode,
  onSubstitute,
  onConfirm,
  onUnconfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [recordedVideoSlot, setRecordedVideoSlot] = useState<ActivitySlot | null>(null);

  // Mirrors instructor-dashboard SlotActionBar: a slot is "online" when it has a
  // Zoom meeting id or a parsed Teams join URL. Teams opens onlineJoinUrl; Zoom
  // builds the join URL from the meeting id.
  const hasOnlineMeeting = (slot: ActivitySlot) => !!(slot.zoomMeetingId || slot.onlineJoinUrl);

  const getMeetingUrl = (slot: ActivitySlot): string | null => {
    if (slot.onlineMeetingType === ONLINE_MEETING_TEAMS && slot.onlineJoinUrl) return slot.onlineJoinUrl;
    if (slot.zoomMeetingId) return `https://zoom.us/j/${slot.zoomMeetingId}`;
    if (slot.onlineJoinUrl) return slot.onlineJoinUrl;
    return null;
  };

  const startMeeting = (slot: ActivitySlot) => {
    const url = getMeetingUrl(slot);
    if (url) window.open(url, '_blank');
  };

  const copyMeetingLink = (slot: ActivitySlot) => {
    const url = getMeetingUrl(slot);
    if (url && navigator.clipboard) navigator.clipboard.writeText(url);
  };

  const batchNameMap = new Map<number, string>();
  for (const b of batchMappings) {
    batchNameMap.set(b.batchId, b.batchCode || b.batchName || `Batch ${b.batchId}`);
  }

  const getBatchNames = (slot: ActivitySlot): string[] => {
    const ids = (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds;
    return ids.map(id => batchNameMap.get(id) || `Batch ${id}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // HH:mm
  };

  const groupedSlots = useMemo(() => {
    const grouped: { [key: string]: ActivitySlot[] } = {};
    (slots || []).forEach(slot => {
      const dateKey = slot.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return grouped;
  }, [slots]);

  const allSlotIds = useMemo(() => (slots || []).map(s => s.id), [slots]);
  const isAllSelected = allSlotIds.length > 0 && selectedIds.size === allSlotIds.length;
  const isSelecting = selectedIds.size > 0;

  const toggleSlot = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allSlotIds));
    }
  };

  const toggleDate = (dateKey: string) => {
    const dateSlotIds = (groupedSlots[dateKey] || []).map(s => s.id);
    const allDateSelected = dateSlotIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allDateSelected) {
        dateSlotIds.forEach(id => next.delete(id));
      } else {
        dateSlotIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (onBulkDelete && selectedIds.size > 0) {
      onBulkDelete(Array.from(selectedIds));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const sortedDateKeys = Object.keys(groupedSlots).sort();

  return (
    <div className="linear-slot-list">
      <div className="section-header">
        <h2>Activity Slots ({slots?.length || 0})</h2>
      </div>

      {/* Selection toolbar */}
      {isEditMode && allSlotIds.length > 0 && (
        <div className={`selection-toolbar ${isSelecting ? 'active' : ''}`}>
          <label className="select-all-label">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={toggleAll}
            />
            {isSelecting
              ? `${selectedIds.size} of ${allSlotIds.length} selected`
              : 'Select all'
            }
          </label>
          {isSelecting && (
            <div className="selection-actions">
              <button className="btn-clear-selection" onClick={clearSelection}>
                Clear
              </button>
              {selectedIds.size === 2 && onSwap && (
                <button className="btn-swap" onClick={() => {
                  const ids = Array.from(selectedIds);
                  onSwap(ids[0], ids[1]);
                }}>
                  Swap 2 Slots
                </button>
              )}
              {onConfirm && (
                <button
                  className="btn-bulk-confirm"
                  onClick={() => {
                    Array.from(selectedIds).forEach((id) => onConfirm(id));
                    clearSelection();
                  }}
                  title="Confirm all selected slots"
                >
                  Confirm {selectedIds.size} slot{selectedIds.size !== 1 ? 's' : ''}
                </button>
              )}
              <button className="btn-bulk-delete" onClick={handleBulkDelete}>
                Delete {selectedIds.size} slot{selectedIds.size !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>
      )}

      {sortedDateKeys.length === 0 ? (
        <div style={{padding: '48px', textAlign: 'center', color: '#9e9e9e'}}>
          No activity slots found. Click "Add Slot" to create one.
        </div>
      ) : (
        sortedDateKeys.map(dateKey => {
          const dateSlotIds = groupedSlots[dateKey].map(s => s.id);
          const allDateSelected = dateSlotIds.length > 0 && dateSlotIds.every(id => selectedIds.has(id));
          const someDateSelected = dateSlotIds.some(id => selectedIds.has(id));

          const dateObj = new Date(dateKey);
          const dayNumber = dateObj.getDate();
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
          const monthYear = dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

          return (
            <div key={dateKey} className="date-group" style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              {/* Left: Date Block */}
              <div style={{
                minWidth: '120px',
                maxWidth: '120px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--surface-card)',
                border: '3px solid #546E7A',
                borderRadius: '12px',
                padding: '16px 12px',
                color: '#37474F',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: '16px',
                alignSelf: 'flex-start'
              }}>
                <div style={{ fontSize: '36px', fontWeight: 700, lineHeight: 1 }}>{dayNumber}</div>
                <div style={{ fontSize: '16px', fontWeight: 500, marginTop: '4px' }}>{dayName}</div>
                <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '2px' }}>{monthYear}</div>
                {isEditMode && (
                  <label style={{ marginTop: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={allDateSelected}
                      ref={el => { if (el) el.indeterminate = someDateSelected && !allDateSelected; }}
                      onChange={() => toggleDate(dateKey)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '12px' }}>Select all</span>
                  </label>
                )}
              </div>

              {/* Right: Slots List */}
              <div className="slot-list" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groupedSlots[dateKey].map(slot => {
                  const isSelected = selectedIds.has(slot.id);
                  const sub = slot.substitution;
                  const isApprovedSub = sub && sub.status === 'APPROVED';
                  const isPendingSub = sub && sub.status === 'REQUESTED';
                  const cardStyle = isApprovedSub
                    ? { borderLeft: '3px solid #b8860b', background: '#fffdf6' }
                    : isPendingSub
                      ? { borderLeft: '3px dashed #b8860b', background: '#fffdf6' }
                      : undefined;
                  const effectiveInstructorName = isApprovedSub && sub?.substituteInstructorName
                    ? sub.substituteInstructorName
                    : slot.instructorName;
                  const effectiveTopicName = isApprovedSub && sub?.substituteTopicName
                    ? sub.substituteTopicName
                    : slot.topicName;
                  return (
                    <div
                      key={slot.id}
                      className={`activity-slot-card ${isEditMode && isSelected ? 'selected' : ''}`}
                      style={{ ...cardStyle, display: 'flex', gap: '12px', alignItems: 'stretch' }}
                    >
                      {isEditMode && (
                        <label className="slot-select-checkbox" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSlot(slot.id)}
                          />
                        </label>
                      )}

                      {/* Left side: Content (50%) */}
                      <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Time and badges row */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div className="time-chip">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </div>
                          {slot.attendanceTaken && (
                            <span className="attendance-done-badge" title="Attendance taken">
                              A
                            </span>
                          )}
                          {sub && (
                            <span
                              title={isApprovedSub
                                ? `Substitution approved${sub.reason ? ` — ${sub.reason}` : ''}`
                                : 'Substitution request pending approval'}
                              style={{
                                padding: '1px 6px',
                                borderRadius: 3,
                                background: 'var(--status-warning-bg)',
                                color: 'var(--status-warning-text)',
                                border: '1px solid #ffeeba',
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                              }}
                            >
                              {isApprovedSub ? 'SUB' : 'SUB?'}
                            </span>
                          )}
                          <span
                            className={`confirmation-pill ${slot.confirmedByUserId != null ? 'confirmed' : 'pending'}`}
                            title={
                              slot.confirmedByUserId != null
                                ? `Confirmed${slot.confirmedAt ? ` ${new Date(slot.confirmedAt).toLocaleString()}` : ''}`
                                : 'Awaiting instructor confirmation — hidden from students'
                            }
                          >
                            {slot.confirmedByUserId != null ? '✓ Confirmed' : 'Awaiting confirmation'}
                          </span>
                        </div>

                        {/* Slot details */}
                        <div className="slot-content" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {/* Instructor (Prominent) */}
                        <div className="instructor" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '16px' }}>👤</span>
                          <span style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-strong)' }}>
                            {effectiveInstructorName || `Instructor ID: ${slot.instructorId}`}
                          </span>
                          {isApprovedSub && sub?.substituteInstructorName && slot.instructorName && (
                            <span style={{ fontSize: 11, color: 'var(--status-warning-text)', fontStyle: 'italic' }}>
                              (was {slot.instructorName})
                            </span>
                          )}
                        </div>

                        {/* Topic (Secondary) */}
                        <div className="topic" style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '2px' }}>
                          <span style={{ fontSize: '13px' }}>📚</span>
                          <span style={{ fontSize: '13px', color: '#424242' }}>{effectiveTopicName || `Topic ID: ${slot.topicMasterId}`}</span>
                          {isApprovedSub && sub?.substituteTopicName && slot.topicName && sub.substituteTopicId !== slot.topicMasterId && (
                            <span style={{ fontSize: 11, color: 'var(--status-warning-text)', fontStyle: 'italic' }}>
                              (was {slot.topicName})
                            </span>
                          )}
                        </div>

                        {/* Metadata (Location, Batches, Meeting) */}
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', marginLeft: '2px' }}>
                          {(slot.roomName || slot.centreName) && (
                            <div className="slot-location" style={{ display: 'flex', gap: '8px' }}>
                              {slot.roomName && <span>🏫 {slot.roomName}</span>}
                              {slot.centreName && <span>📍 {slot.centreName}</span>}
                            </div>
                          )}
                          {batchMappings.length > 0 && (
                            <div className="slot-batches" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {getBatchNames(slot).map((name, idx) => (
                                <span key={idx} className="slot-batch-tag">{name}</span>
                              ))}
                            </div>
                          )}
                          {hasOnlineMeeting(slot) && (
                            <span style={{
                              fontSize: '12px',
                              color: '#1976d2',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#e3f2fd',
                              padding: '2px 8px',
                              borderRadius: '3px',
                              fontWeight: 500
                            }}>
                              {slot.onlineMeetingType === ONLINE_MEETING_TEAMS ? '📹' : '🔗'}
                              {slot.onlineMeetingType === ONLINE_MEETING_TEAMS
                                ? 'Teams'
                                : `Zoom ${slot.zoomMeetingId}`}
                            </span>
                          )}
                        </div>

                        {/* Substitution agenda */}
                        {isApprovedSub && sub?.substituteAgenda && (
                          <div style={{ fontSize: 12, color: '#5d4037', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 3, padding: '4px 8px' }}>
                            <strong>Today's plan:</strong> {sub.substituteAgenda}
                          </div>
                        )}
                        {/* Regular agenda */}
                        {!isApprovedSub && slot.agenda && (
                          <div className="agenda" style={{fontSize: '13px', color: '#9e9e9e'}}>
                            {slot.agenda}
                          </div>
                        )}
                        </div>
                      </div>

                      {/* Right side: Actions (50%, wrappable) */}
                      <div className="actions" style={{
                        flex: '0 1 50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'flex-end'
                      }}>
                        {/* Top row: Edit/Delete icons only */}
                        {isEditMode && !slot.attendanceTaken && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              className="btn-edit"
                              onClick={() => onEdit(slot)}
                              title="Edit slot"
                              style={{
                                padding: '6px 10px',
                                fontSize: '16px',
                                background: 'var(--surface-sunken)',
                                border: '1px solid var(--border-default)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => onDelete(slot.id)}
                              title="Delete slot"
                              style={{
                                padding: '6px 10px',
                                fontSize: '16px',
                                background: 'var(--surface-sunken)',
                                border: '1px solid var(--border-default)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        )}

                        {/* Other action buttons (wrappable) */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          justifyContent: 'flex-end',
                          width: '100%'
                        }}>
                        {hasOnlineMeeting(slot) && (
                          <>
                            <button
                              className="btn-start-meeting"
                              onClick={() => startMeeting(slot)}
                              title="Start Meeting"
                            >
                              Start Meeting
                            </button>
                            <button
                              className="btn-copy-link"
                              onClick={() => copyMeetingLink(slot)}
                              title="Copy Meeting Link"
                              style={{ padding: '6px 12px' }}
                            >
                              ⧉
                            </button>
                            <button
                              className="btn-recorded-video"
                              onClick={() => setRecordedVideoSlot(slot)}
                              title="Recorded Video"
                            >
                              Recorded Video
                            </button>
                          </>
                        )}
                        {/* Java attendance button hidden — Node attendance is now the canonical "Attendance" button.
                            Functionality preserved via onAttendance callback; only the button is hidden. */}
                        {/* {onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                          <button className="btn-attendance" onClick={() => onAttendance(
                            (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                            slot.id,
                            slot.instructorId,
                            slot.instructorName
                          )} title="Take Attendance (Java)">
                            Attendance
                          </button>
                        )} */}
                        {onAttendanceNode && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                          <button className="btn-attendance" onClick={() => onAttendanceNode(
                            (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                            slot.id,
                            slot.instructorId,
                            slot.instructorName
                          )} title="Take Attendance" style={{ background: 'var(--brand-primary)', color: 'var(--text-on-brand)', borderColor: 'var(--brand-primary)' }}>
                            Attendance
                          </button>
                        )}
                        {onSubstitute && !slot.attendanceTaken && (
                          <button
                            className="btn-substitute"
                            onClick={() => onSubstitute(slot)}
                            title="Assign substitute teacher"
                          >
                            Substitute
                          </button>
                        )}
                        {/* Confirm/Unconfirm are not gated by isEditMode — schedulers
                            may want to confirm-on-behalf without entering edit mode. */}
                        {onConfirm && slot.confirmedByUserId == null && (
                          <button
                            className="btn-confirm-slot"
                            onClick={() => onConfirm(slot.id)}
                            title="Confirm this slot — needed before students can see it"
                          >
                            Confirm
                          </button>
                        )}
                        {onUnconfirm && slot.confirmedByUserId != null && (
                          <button
                            className="btn-unconfirm-slot"
                            onClick={() => onUnconfirm(slot.id)}
                            title="Revoke confirmation"
                          >
                            Unconfirm
                          </button>
                        )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {isEditMode && (
                  <button className="add-slot-btn" onClick={() => onAdd(dateKey)} style={{ marginTop: '8px' }}>
                    + Add Slot for this Date
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {isEditMode && sortedDateKeys.length === 0 && (
        <button className="add-slot-btn" style={{marginTop: '16px'}} onClick={() => onAdd()}>
          + Add First Activity Slot
        </button>
      )}

      {recordedVideoSlot && (
        <RecordedVideoModal
          show={!!recordedVideoSlot}
          onClose={() => setRecordedVideoSlot(null)}
          activitySlotTemplateId={recordedVideoSlot.id}
          batchId={(recordedVideoSlot.batchIds && recordedVideoSlot.batchIds[0]) ?? null}
          topicName={recordedVideoSlot.topicName}
        />
      )}
    </div>
  );
};

export default LinearSlotList;
